/**
 * StickyCartBar - Sticky bottom bar that appears when scrolled past add button
 */
import { memo } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
import { Button } from '../ui/button';

const popAnimation = {
  initial: { scale: 1 },
  pop: { 
    scale: [1, 1.2, 0.9, 1.1, 1],
    transition: { duration: 0.4, ease: "easeInOut" }
  }
};

const StickyCartBar = memo(function StickyCartBar({ 
  item, 
  selectedSize, 
  quantity, 
  finalPrice, 
  isAddingToCart,
  onAddToCart 
}) {
  const totalPrice = (finalPrice * quantity).toFixed(2);

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-card/95 backdrop-blur-lg border-t border-border shadow-2xl"
      data-testid="sticky-add-to-cart-bar"
    >
      <div className="max-w-lg mx-auto">
        {/* Item summary */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {item.image && (
              <img 
                src={item.image} 
                alt={item.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            )}
            <div>
              <p className="font-medium text-foreground text-sm line-clamp-1">{item.name}</p>
              <p className="text-xs text-muted-foreground">
                {selectedSize && `${selectedSize} • `}
                Qty: {quantity}
              </p>
            </div>
          </div>
          <span className="font-bold text-primary text-lg">
            ${totalPrice}
          </span>
        </div>
        
        {/* Centered Add to Cart button */}
        <div className="flex justify-center">
          <motion.div 
            className="w-full max-w-sm"
            animate={isAddingToCart ? popAnimation.pop : popAnimation.initial}
          >
            <Button
              onClick={onAddToCart}
              className="w-full rounded-full bg-primary hover:bg-primary/90 h-14 text-base font-semibold shadow-lg"
              data-testid="sticky-confirm-add-to-cart"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Add {quantity} to Order • {totalPrice}
            </Button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
});

export default StickyCartBar;
